import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, CircularProgress, Button } from '@mui/material';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import DynamicTable from '../../components/DynamicTable/DynamicTable';
import { employerApi } from '../../services/apiClient';
import { useCompanyContext } from '../../context/CompanyContext';

interface EmployerSalarySacrificeReport {
  id: string; // e.g. "JAN_2025"
  companyID: string;
  timestampGenerated?: string;
  totalValue?: number;
  auditLog?: any[];
}

const Payroll = () => {
  const { activeCompany } = useCompanyContext();
  const [reports, setReports] = useState<EmployerSalarySacrificeReport[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const companyID = activeCompany?.companyID;

  const {
    data: reportsResponse,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['salarySacrifice', companyID],
    queryFn: async () => {
      const response = await employerApi.get('/payroll');
      return response;
    },
    enabled: Boolean(companyID),
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Update reports when response changes
  useEffect(() => {
    if (reportsResponse) {
      const data = reportsResponse?.data?.data || reportsResponse?.data || [];
      setReports(Array.isArray(data) ? data : []);
    } else if (!loading) {
      // Only clear if we're not loading (to avoid clearing during refetch)
      setReports([]);
    }
  }, [reportsResponse, loading]);

  const handleDownload = async (reportId: string) => {
    try {
      setDownloadingId(reportId);
      // Use the axios instance directly to get the full response with blob
      // This bypasses the apiClient wrapper which might interfere with blob handling
      const axiosInstance = employerApi.getInstance();
      const response = await axiosInstance.get(
        `/payroll/${reportId}/download`,
        {
          responseType: 'blob'
        }
      );

      // response.data is already a Blob when responseType is 'blob'
      // No need to wrap it in new Blob() - use it directly
      const blob = response.data;

      // Verify it's actually a Blob
      if (!(blob instanceof Blob)) {
        throw new Error('Invalid response format');
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to download salary sacrifice report', err);
      // Try to extract error message from blob response if it's an error JSON
      let errorMessage = 'Failed to download report. Please try again.';
      if (err.response?.data) {
        try {
          // If error response is a blob (JSON error), convert it to text
          if (err.response.data instanceof Blob) {
            const text = await err.response.data.text();
            const errorJson = JSON.parse(text);
            errorMessage = errorJson.message || errorMessage;
          } else if (typeof err.response.data === 'object') {
            errorMessage = err.response.data.message || errorMessage;
          }
        } catch {
          // If parsing fails, use default message
        }
      }
      toast.error(errorMessage);
    } finally {
      setDownloadingId(null);
    }
  };

  const rows = reports.map((report) => {
    const [month, year] = (report.id || '').split('_');

    return [
      report.id,
      `${month || ''} ${year || ''}`,
      report.timestampGenerated,
      report.totalValue != null ? `£${report.totalValue.toFixed(2)}` : '',
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          handleDownload(report.id);
        }}
        disabled={downloadingId === report.id}
      >
        {downloadingId === report.id ? (
          <CircularProgress size={16} />
        ) : (
          'Download'
        )}
      </Button>
    ];
  });

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Payroll Salary Sacrifice Reports
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, fontWeight: 400 }}>
        View and manage salary sacrifice reports for your company. Any new reports will appear here when they are generated.
      </Typography>
      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DynamicTable
            tableId="EmployerSalarySacReportsTable"
            cols={['Report ID', 'Period', 'Date Issued', 'Total Value', 'Actions']}
            rows={rows}
            loading={loading}
            idIndexInColumns={0}
            showID={false}
            onRowClicked={() => { }}
          />
        )}
      </Paper>
    </Box>
  );
};

export default Payroll;

